import { arrInc } from '../../../fun-js-lib/arr'
import { canvasCanTextFit, canvasFonted, canvasSplitBounded, canvasTextBounded } from '../../../fun-js-lib/canvas'
import Dot from '../../../fun-js-lib/geometry/Dot'
import Rect from '../../../fun-js-lib/geometry/Rect'
import { splitBounded, drawLine } from '../../canvasDraw'
import { shiftsRect } from './shifts'

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {import('../../constructionLog').LogPage} page 
 */
export function fillMechanizationContent (context, page) {
  canvasFonted(context, optimalFont(context, page), () => {
    drawText(context, page)
  })
  drawDiagonalLine(context, page)
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {import('../../constructionLog').LogPage} page 
 */
function optimalFont (context, page) {
  let optimalParams = getOptimalParams(context, page)
  return optimalParams.fontSize + 'px Arial'
}

/**
 * @param {import('../../constructionLog').LogPage} page 
 */
function getText (page) {
  return page.shifts[0].mechanization
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {import('../../constructionLog').LogPage} page 
 */
function getOptimalParams (context, page) {
  let possible = MechanizationTextParams.possibleParamss()
  const text = getText(page)
  const maxWidth = mechanizationContentRect().w - mechanizationTextPadding() * 2
  let optimal = possible.find(params => canvasCanTextFit({
    context,
    text,
    font: params.fontSize + 'px Arial',
    maxNLines: params.nLines,
    maxWidth
  }))

  // Adjust number of lines based on number of lines actually filled.
  // This is relevant when one word in mechanization content is really long,
  // so it makes overall font small. In such case it is possible to fill
  // only some of 9 lines and those lines are grouped on the top (which is 
  // undesired behavior). To fix this:
  let nFilledLines = 0
  canvasFonted(context, optimal.fontSize + 'px Arial', () => {
    nFilledLines = canvasSplitBounded(context, text, maxWidth).length
  })
  if (nFilledLines <= 3) {
    optimal.nLines = 3
  } else if (nFilledLines <= 6) {
    optimal.nLines = 6
  }
  return optimal
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {import('../../constructionLog').LogPage} page 
 */
function getLineInterval (context, page) {
  let contentRect = mechanizationContentRect()
  let optimalParams = getOptimalParams(context, page)
  return contentRect.h / optimalParams.nLines
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {import('../../constructionLog').LogPage} page 
 */
function drawText (context, page) {
  let lineInterval = getLineInterval(context, page)
  let text = getText(page)
  let textRect = getTextRect(context, page)
  canvasTextBounded(context, text, textRect, lineInterval)
}

/**
 * Draws diagonal line that crosses blank space under mechanization content text
 * @param {CanvasRenderingContext2D} context 
 * @param {import('../../constructionLog').LogPage} page 
 */
function drawDiagonalLine (context, page) {
  let contentRect = mechanizationContentRect()
  let nLines = getNLines(context, page)
  let start = new Dot({ x: contentRect.x + 7, y: contentRect.y + contentRect.h - 3 })
  let optimalParams = getOptimalParams(context, page)
  if (getText(page) === '') {
    let end = new Dot({ x: contentRect.x + contentRect.w - 2, y: contentRect.y + 7 })
    drawLine(context, start, end, 1)
  } else if (nLines !== optimalParams.nLines) {
    let lineInterval = getLineInterval(context, page)
    let textHeight = nLines * lineInterval
    let textRect = getTextRect(context, page)
    let end = new Dot({ x: contentRect.x + contentRect.w - 2, y: textRect.y + textHeight + 4 })
    drawLine(context, start, end, 1)
  }
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {import('../../constructionLog').LogPage} page 
 */
function getNLines (context, page) {
  let text = getText(page)
  let textRect = getTextRect(context, page)
  let length = 0
  canvasFonted(context, optimalFont(context, page), () => {
    length = splitBounded(context, text, textRect.w).length
  })
  return length
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {import('../../constructionLog').LogPage} page 
 */
function getTextRect (context, page) {
  let contentRect = mechanizationContentRect()
  const smallYAdjustment = -3
  return Rect.plain({
    x: contentRect.x + mechanizationTextPadding(),
    y: contentRect.y + smallYAdjustment + getYShift(context, page),
    w: contentRect.w - 2 * mechanizationTextPadding(),
    h: cellHeight * 3
  })
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {import('../../constructionLog').LogPage} page 
 */
function getYShift (context, page) {
  let optimalParams = getOptimalParams(context, page)
  if (optimalParams.nLines === 3) {
    const centeringAdjustment = -cellHeight / 2 + optimalParams.fontSize / 2
    return centeringAdjustment
  } else if (optimalParams.nLines === 6) {
    const smallAdjustment = -4
    return smallAdjustment
  } else {
    return 0
  }
}

class MechanizationTextParams {
  /**
   * @param {number} nLines 
   * @param {number} fontSize 
   */
  constructor (nLines, fontSize) {
    this.nLines = nLines
    this.fontSize = fontSize
  }

  static possibleParamss () {
    let arr = [
      [3, 44], [3, 40], [3, 36], [3, 30], [3, 28],
      [6, 36], [6, 30], [6, 28]]
      .concat(arrInc(24).map(x => [9, 24 - x]))
    return arr.map(raw => new MechanizationTextParams(raw[0], raw[1]))
  }
}

function mechanizationTextPadding () {
  return 10
}

const cellHeight = 80

function mechanizationContentRect () {
  let rect = shiftsRect()
  return Rect.plain({
    x: rect.x + 190 + 480,
    y: rect.y + 120,
    w: 280,
    h: cellHeight * 3
  })
}